import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "flex items-center gap-3 mb-6 flex-wrap" }
const _hoisted_2 = { class: "flex gap-1 text-sm" }
const _hoisted_3 = ["onClick"]
const _hoisted_4 = { class: "ml-auto flex gap-2" }
const _hoisted_5 = {
  key: 0,
  class: "text-muted text-sm"
}
const _hoisted_6 = {
  key: 1,
  class: "flex flex-col gap-6",
  "aria-hidden": "true"
}
const _hoisted_7 = { class: "flex items-center gap-3 mb-2" }
const _hoisted_8 = { class: "text-lg" }
const _hoisted_9 = { class: "text-xs text-muted" }
const _hoisted_10 = {
  key: 2,
  class: "flex flex-col gap-6"
}
const _hoisted_11 = { key: 1 }
const _hoisted_12 = { class: "flex items-center gap-3 mb-2" }
const _hoisted_13 = { class: "text-lg" }
const _hoisted_14 = {
  key: 0,
  class: "text-xs text-muted"
}
const _hoisted_15 = { class: "ml-auto flex gap-3 text-sm" }
const _hoisted_16 = ["onClick"]
const _hoisted_17 = {
  key: 3,
  class: "mt-8"
}
const _hoisted_18 = { class: "mt-2 text-sm divide-y divide-accent" }
const _hoisted_19 = { class: "flex-grow truncate" }
const _hoisted_20 = ["onClick"]

import type { DevRoeTalkGroup } from '#shared/lex'
import type { TalkEntry } from '~~/app/components/admin/TalkDraggable.vue'

interface TalkGroupEntry {
  rkey: string
  uri: string
  cid: string
  value: DevRoeTalkGroup.Record
}

type TimelineEntry
  = | { kind: 'talk', sortDate: string, talk: TalkEntry }
    | { kind: 'group', sortDate: string, group: TalkGroupEntry, talks: TalkEntry[] }


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

definePageMeta({ layout: false })
useHead({ title: 'Talks - admin - Daniel Roe' })

const { data: talksData, refresh: refreshTalks, status: talksStatus } = useAdminFetch<TalkEntry[]>('/api/admin/talks', { default: () => [] })
const { data: groupsData, refresh: refreshGroups, status: groupsStatus } = useAdminFetch<TalkGroupEntry[]>('/api/admin/talk-groups', { default: () => [] })

const showSkeleton = computed(() =>
  (talksStatus.value === 'pending' || groupsStatus.value === 'pending')
  && !talksData.value.length && !groupsData.value.length,
)

const filter = ref<'all' | 'past' | 'upcoming'>('all')
const now = new Date().toISOString()

const formatter = new Intl.DateTimeFormat('en', { dateStyle: 'medium' })

function inFilter (talk: TalkEntry): boolean {
  if (filter.value === 'past') return talk.value.date < now
  if (filter.value === 'upcoming') return talk.value.date >= now
  return true
}

/** Filter-aware bucket of talks by group URI; `''` collects top-level. */
const bucketed = computed(() => {
  const byGroup = new Map<string, TalkEntry[]>()
  byGroup.set('', [])
  for (const g of groupsData.value ?? []) byGroup.set(g.uri, [])

  for (const t of talksData.value ?? []) {
    if (!inFilter(t)) continue
    const key = t.value.group?.uri ?? ''
    const bucket = byGroup.get(key)
    if (bucket) bucket.push(t)
  }

  for (const bucket of byGroup.values()) {
    bucket.sort((a, b) => b.value.date.localeCompare(a.value.date))
  }
  return byGroup
})

// Date-desc timeline. A group's `sortDate` is its freshest child talk,
// falling back to `createdAt` so empty groups surface at the top rather
// than vanishing to the bottom.
const timeline = computed<TimelineEntry[]>(() => {
  const entries: TimelineEntry[] = []

  for (const talk of bucketed.value.get('') ?? []) {
    entries.push({ kind: 'talk', sortDate: talk.value.date, talk })
  }

  for (const group of groupsData.value ?? []) {
    const talks = bucketed.value.get(group.uri) ?? []
    const sortDate = talks[0]?.value.date ?? group.value.createdAt
    entries.push({ kind: 'group', sortDate, group, talks })
  }

  entries.sort((a, b) => b.sortDate.localeCompare(a.sortDate))
  return entries
})

async function putTalk (talk: TalkEntry, mutate: (value: TalkEntry['value']) => void) {
  const value = { ...talk.value }
  mutate(value)
  delete (value as Record<string, unknown>).$type
  const previous = talksData.value
  talksData.value = talksData.value.map(t =>
    t.rkey === talk.rkey ? { ...t, value: { ...value, $type: talk.value.$type } } : t,
  )
  try {
    await $fetch(`/api/admin/talks/${talk.rkey}`, { method: 'PUT', body: value })
    refreshTalks()
  }
  catch (error) {
    talksData.value = previous
    throw error
  }
}

async function makeGroupFromTalk (talk: TalkEntry) {
  if (!talk.value.title) return

  const created = await $fetch<{ rkey: string, uri: string, cid: string }>(
    '/api/admin/talk-groups',
    {
      method: 'POST',
      body: {
        title: talk.value.title,
        ...(talk.value.description ? { description: talk.value.description } : {}),
      },
    },
  )

  await refreshGroups()
  await putTalk(talk, v => {
    v.group = { uri: created.uri, cid: created.cid }
  })
}

// `targetUri` is the destination `<ul>`'s `data-target-uri`: a group's
// at:// URI, or empty string meaning "make this top-level".
function onDropped (talk: TalkEntry, targetUri: string) {
  if (targetUri) {
    if (talk.value.group?.uri === targetUri) return
    const target = (groupsData.value ?? []).find(g => g.uri === targetUri)
    if (!target) return
    putTalk(talk, v => {
      v.group = { uri: target.uri, cid: target.cid }
    })
  }
  else {
    if (!talk.value.group) return
    putTalk(talk, v => {
      delete v.group
    })
  }
}

function promoteToTopLevel (talk: TalkEntry) {
  if (!talk.value.group) return
  putTalk(talk, v => {
    delete v.group
  })
}

async function deleteTalk (talk: TalkEntry) {
  if (!confirm(`Delete "${talk.value.title || talk.value.source}"?`)) return
  const previous = talksData.value
  talksData.value = talksData.value.filter(t => t.rkey !== talk.rkey)
  try {
    await $fetch(`/api/admin/talks/${talk.rkey}`, { method: 'DELETE' })
    refreshTalks()
  }
  catch (error) {
    talksData.value = previous
    throw error
  }
}

async function deleteGroup (group: TalkGroupEntry) {
  const childCount = (bucketed.value.get(group.uri) ?? []).length
  const message = childCount
    ? `Delete group "${group.value.title}"? ${childCount} talk(s) in it will become top-level.`
    : `Delete group "${group.value.title}"?`
  if (!confirm(message)) return

  // Promote children to top-level first so their strong-refs don't dangle.
  const children = bucketed.value.get(group.uri) ?? []
  const previousTalks = talksData.value
  const previousGroups = groupsData.value
  const childKeys = new Set(children.map(c => c.rkey))
  talksData.value = talksData.value.map(t => {
    if (!childKeys.has(t.rkey)) return t
    const value = { ...t.value }
    delete value.group
    return { ...t, value }
  })
  groupsData.value = groupsData.value.filter(g => g.rkey !== group.rkey)
  try {
    for (const child of children) {
      const value = { ...child.value }
      delete value.group
      delete (value as Record<string, unknown>).$type
      await $fetch(`/api/admin/talks/${child.rkey}`, { method: 'PUT', body: value })
    }
    await $fetch(`/api/admin/talk-groups/${group.rkey}`, { method: 'DELETE' })
    refreshTalks()
    refreshGroups()
  }
  catch (error) {
    talksData.value = previousTalks
    groupsData.value = previousGroups
    throw error
  }
}

// Each top-level talk lives in its own single-item draggable list so
// the shared drag group works the same way as it does for in-group talks.
function singletonList (talk: TalkEntry): TalkEntry[] {
  return [talk]
}

return (_ctx: any,_cache: any) => {
  const _component_NuxtLink = _resolveComponent("NuxtLink")!
  const _component_AdminSkeletonRows = _resolveComponent("AdminSkeletonRows")!
  const _component_AdminSkeleton = _resolveComponent("AdminSkeleton")!
  const _component_AdminTalkDraggable = _resolveComponent("AdminTalkDraggable")!
  const _component_AdminShell = _resolveComponent("AdminShell")!

  return (_openBlock(), _createBlock(_component_AdminShell, { title: "Talks" }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(['all', 'past', 'upcoming'] as const, (f) => {
            return _createElementVNode("button", {
              key: f,
              type: "button",
              class: _normalizeClass([_unref(filter) === f ? 'bg-primary text-background' : 'bg-accent text-muted', "px-3 py-1 capitalize hover:opacity-80 transition-opacity"]),
              onClick: ($event: any) => (filter.value = f)
            }, _toDisplayString(f), 11, _hoisted_3)
          }), 64))
        ]),
        _createElementVNode("div", _hoisted_4, [
          _createVNode(_component_NuxtLink, {
            to: "/admin/talk-groups/new",
            class: "text-sm bg-accent text-primary px-3 py-1 hover:bg-accent/80 transition-colors"
          }, {
            default: _withCtx(() => [...(_cache[0] || (_cache[0] = [
              _createTextVNode(" New group ", -1)
            ]))]),
            _: 1
          }),
          _createVNode(_component_NuxtLink, {
            to: "/admin/talks/new",
            class: "text-sm bg-primary text-background px-3 py-1 hover:bg-primary/90 transition-colors"
          }, {
            default: _withCtx(() => [...(_cache[1] || (_cache[1] = [
              _createTextVNode(" New talk ", -1)
            ]))]),
            _: 1
          })
        ])
      ]),
      (!_unref(showSkeleton) && !_unref(timeline).length)
        ? (_openBlock(), _createElementBlock("p", _hoisted_5, " No talks yet. "))
        : _createCommentVNode("", true),
      (_unref(showSkeleton))
        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
            _createVNode(_component_AdminSkeletonRows, {
              variant: "card",
              meta: "xs",
              rows: 2
            }),
            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(2, (i) => {
              return _createElementVNode("section", { key: i }, [
                _createElementVNode("header", _hoisted_7, [
                  _createElementVNode("h2", _hoisted_8, [
                    _createVNode(_component_AdminSkeleton, { class: "bg-accent w-40" })
                  ]),
                  _createElementVNode("span", _hoisted_9, [
                    _createVNode(_component_AdminSkeleton, { class: "bg-accent w-28" })
                  ])
                ]),
                _createVNode(_component_AdminSkeletonRows, {
                  variant: "card",
                  meta: "xs",
                  rows: 3
                })
              ])
            }), 64))
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_10, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(timeline), (entry) => {
              return (_openBlock(), _createElementBlock(_Fragment, {
                key: entry.kind === 'group' ? entry.group.rkey : entry.talk.rkey
              }, [
                (entry.kind === 'talk')
                  ? (_openBlock(), _createBlock(_component_AdminTalkDraggable, {
                      key: 0,
                      talks: singletonList(entry.talk),
                      "top-level": true,
                      onDropped: onDropped,
                      onMakeGroup: makeGroupFromTalk
                    }, null, 8, ["talks"]))
                  : (_openBlock(), _createElementBlock("section", _hoisted_11, [
                      _createElementVNode("header", _hoisted_12, [
                        _createElementVNode("h2", _hoisted_13, _toDisplayString(entry.group.value.title), 1),
                        (entry.talks.length)
                          ? (_openBlock(), _createElementBlock("span", _hoisted_14, " most recent " + _toDisplayString(_unref(formatter).format(new Date(entry.talks[0]!.value.date))), 1))
                          : _createCommentVNode("", true),
                        _createElementVNode("div", _hoisted_15, [
                          _createVNode(_component_NuxtLink, {
                            to: `/admin/talk-groups/${entry.group.rkey}`,
                            class: "text-muted hover:text-primary transition-colors"
                          }, {
                            default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                              _createTextVNode(" Edit ", -1)
                            ]))]),
                            _: 1
                          }, 8, ["to"]),
                          _createElementVNode("button", {
                            type: "button",
                            class: "text-muted hover:text-red-500 transition-colors",
                            onClick: ($event: any) => (deleteGroup(entry.group))
                          }, " Delete ", 8, _hoisted_16)
                        ])
                      ]),
                      _createVNode(_component_AdminTalkDraggable, {
                        talks: entry.talks,
                        "show-promote": true,
                        "force-title-from-source": true,
                        "target-uri": entry.group.uri,
                        "empty-hint": "Drag talks here.",
                        onDropped: onDropped,
                        onPromote: promoteToTopLevel
                      }, null, 8, ["talks", "target-uri"])
                    ]))
              ], 64))
            }), 128))
          ])),
      (_unref(talksData)?.length)
        ? (_openBlock(), _createElementBlock("details", _hoisted_17, [
            _cache[3] || (_cache[3] = _createElementVNode("summary", { class: "text-xs text-muted cursor-pointer" }, " Delete a talk ", -1)),
            _createElementVNode("ul", _hoisted_18, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList([...(_unref(talksData) ?? [])].sort((a, b) => b.value.date.localeCompare(a.value.date)), (talk) => {
                return (_openBlock(), _createElementBlock("li", {
                  key: talk.rkey,
                  class: "py-2 flex items-center gap-3"
                }, [
                  _createElementVNode("span", _hoisted_19, _toDisplayString(talk.value.title || talk.value.source), 1),
                  _createElementVNode("button", {
                    type: "button",
                    class: "text-muted hover:text-red-500",
                    onClick: ($event: any) => (deleteTalk(talk))
                  }, " Delete ", 8, _hoisted_20)
                ]))
              }), 128))
            ])
          ]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})